import type { To } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { ROUTE_PATHS } from '../routes/routePaths';
import { getSharedRedirect } from '../services/commonServices';
import { getErrorLogCorrelationId } from '../services/errorLogger';
import type { GetSharedRedirectResponse } from '../types/common';
import { getApiErrorMessage } from './apiError';
import { logger } from './logger';

type RouteKey = keyof typeof ROUTE_PATHS;

export type SharedRedirectNavigation =
  | { kind: 'new-tab' }
  | {
      kind: 'same-tab-spa';
      navigate: (to: To) => void;
      /**
       * When true, do not hard-redirect inside this helper on API error.
       * Caller can perform side-effects (e.g. logging) first, then navigate.
       */
      deferHardRedirect?: boolean;
      /**
       * Called with the shared-redirect API error before any error redirect, so a
       * deferred caller can forward its already-logged correlationId
       * (getErrorLogCorrelationId) to /standard-error instead of a fresh one.
       */
      onApiError?: (error: unknown) => void;
    };

/**
 * Parses `eURL` / `eUrl` / `eurl` from a CFM-style href, calls `commons/shared-redirect`, then navigates
 * using the same rules as {@link LinkInterceptorContext} (GRID_VIEW adds `keyvalue=pub`).
 */
export async function runSharedRedirectFromCfmHref(
  href: string,
  navigation: SharedRedirectNavigation,
): Promise<
  | 'ok'
  | 'no-eurl'
  | 'no-mapping'
  | 'hard-redirect'
  | 'hard-redirect-standard-error'
  | 'hard-redirect-category-home'
> {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return 'no-eurl';
  }

  const encryptedUrlParameter =
    url.searchParams.get('eURL') ??
    url.searchParams.get('eUrl') ??
    url.searchParams.get('eurl');

  if (!encryptedUrlParameter) {
    return 'no-eurl';
  }

  try {
    const data: GetSharedRedirectResponse =
      await getSharedRedirect(encryptedUrlParameter);
    const rawEnum = data?.data?.redirectEnum;
    const redirectEnum =
      typeof rawEnum === 'string' ? rawEnum.trim() : '';
    const redirectedEurl = data?.data?.eurl ?? null;

    // Some targets (e.g. MY_ALERT) only return redirectEnum; eurl may be null.
    const hasRouteKey =
      redirectEnum !== '' &&
      Object.prototype.hasOwnProperty.call(ROUTE_PATHS, redirectEnum);

    if (hasRouteKey) {
      const routePath = ROUTE_PATHS[redirectEnum as RouteKey];
      const parameters = new URLSearchParams();
      if (redirectedEurl) {
        parameters.set('eurl', redirectedEurl);
      }
      if (routePath === ROUTE_PATHS.GRID_VIEW) {
        parameters.set('keyvalue', 'pub');
      }
      const queryString = parameters.toString();
      const search = queryString ? `?${queryString}` : '';

      if (navigation.kind === 'new-tab') {
        const redirectUrl = `${routePath}${search}`;
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigation.navigate({ pathname: routePath, search });
      }
      return 'ok';
    }
    return 'no-mapping';
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; errors?: Array<{ message?: string }> }>;
    // The API puts its message in `errors[0].message` (e.g. "Invalid eurl"); a
    // missing/blank one falls back to the top-level `message`.
    const apiMessage = getApiErrorMessage(axiosError.response?.data) ?? 'Invalid request';
    logger.error('Error fetching redirect data:', apiMessage);
    if (navigation.kind === 'same-tab-spa') {
      navigation.onApiError?.(error);
    }

    if (axiosError?.response?.status === 400) {
      const encodedMessage = encodeURIComponent(apiMessage);
      if (navigation.kind === 'same-tab-spa' && navigation.deferHardRedirect) {
        return 'hard-redirect-standard-error';
      }
      // Carry the correlationId the axios interceptor already logged for this 400
      // so /standard-error shows/emails that same Ref. instead of minting a new one.
      const correlationId = getErrorLogCorrelationId(error);
      const correlationQuery = correlationId
        ? `&correlationId=${encodeURIComponent(correlationId)}`
        : '';
      window.location.assign(
        `${ROUTE_PATHS.STANDARD_ERROR}?message=${encodedMessage}${correlationQuery}`,
      );
      return 'hard-redirect';
    } else {
      if (navigation.kind === 'same-tab-spa' && navigation.deferHardRedirect) {
        return 'hard-redirect-category-home';
      }
      window.location.assign(ROUTE_PATHS.CATEGORY_HOME);
      return 'hard-redirect';
    }
  }
}
